import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/cars/images/eb666297-f67d-4758-995c-0702d5dcbde1.png`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-white/95 backdrop-blur-sm py-4'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img
              src={LOGO_URL}
              alt="Shanghai RunTheRays International Trade Co., Ltd."
              className="h-10 sm:h-14 md:h-16 w-auto"
              data-testid="header-logo"
            />
            <div className="text-sm leading-tight sm:text-xl lg:text-2xl font-bold text-blue-900">
              Shanghai RunTheRays <span className="text-orange-600">Int. Trade Co. Ltd.</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors duration-200"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors duration-200"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('industries')}
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors duration-200"
            >
              Industries
            </button>
            <button
              onClick={() => scrollToSection('approach')}
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors duration-200"
            >
              Our Approach
            </button>
            <a
              href="/blog"
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors duration-200"
              data-testid="nav-blog-link"
            >
              Blog
            </a>
            <a
              href="/downloads"
              className="text-gray-700 hover:text-blue-900 font-medium transition-colors duration-200"
              data-testid="nav-downloads-link"
            >
              Downloads
            </a>
            <a
              href="/sourcing"
              className="text-orange-600 hover:text-orange-700 font-semibold transition-colors duration-200"
              data-testid="nav-sourcing-link"
            >
              Sourcing Portal
            </a>
            <Button
              onClick={() => scrollToSection('quote')}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-2 transition-colors duration-200"
            >
              Get Quote
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-blue-900"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-4 border-t pt-4">
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-gray-700 hover:text-blue-900 font-medium py-2"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="block w-full text-left text-gray-700 hover:text-blue-900 font-medium py-2"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('industries')}
              className="block w-full text-left text-gray-700 hover:text-blue-900 font-medium py-2"
            >
              Industries
            </button>
            <button
              onClick={() => scrollToSection('approach')}
              className="block w-full text-left text-gray-700 hover:text-blue-900 font-medium py-2"
            >
              Our Approach
            </button>
            <a
              href="/blog"
              className="block w-full text-left text-gray-700 hover:text-blue-900 font-medium py-2"
            >
              Blog
            </a>
            <a
              href="/downloads"
              className="block w-full text-left text-gray-700 hover:text-blue-900 font-medium py-2"
            >
              Downloads
            </a>
            <a
              href="/sourcing"
              className="block w-full text-left text-orange-600 hover:text-orange-700 font-semibold py-2"
            >
              Sourcing Portal
            </a>
            <Button
              onClick={() => scrollToSection('quote')}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold"
            >
              Get Quote
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;